import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3167/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor: attach JWT token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor: redirect on 401
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('auth_token');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API (uses raw axios, not the intercepted instance, for public endpoints)
export const authAPI = {
    status: () => axios.get(`${API_URL}/auth/status`),
    me: (token) => axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
    }),
};

export const zonesAPI = {
    getAll: () => api.get('/zones'),
    getById: (zoneId) => api.get(`/zones/${zoneId}`),
    create: (domain) => api.post('/zones', { name: domain }),
    delete: (zoneId) => api.delete(`/zones/${zoneId}`),
    getRecords: (zoneId) => api.get(`/zones/${zoneId}/records`),
    createRecord: (zoneId, record) => api.post(`/zones/${zoneId}/records`, record),
    updateRecord: (zoneId, recordId, record) => api.put(`/zones/${zoneId}/records/${recordId}`, record),
    deleteRecord: (zoneId, recordId) => api.delete(`/zones/${zoneId}/records/${recordId}`),
    bulkCreateRecords: (zoneId, records) => api.post(`/zones/${zoneId}/records/bulk`, { records }),
    exportRecords: (zoneId, format) => api.get(`/zones/${zoneId}/export?format=${format}`),
    fixCNAMEs: (zoneId) => api.post(`/zones/${zoneId}/fix-cnames`),
    getSettings: (zoneId) => api.get(`/zones/${zoneId}/settings`),
    updateSetting: (zoneId, settingId, value) => api.patch(`/zones/${zoneId}/settings/${settingId}`, { value }),
    getAnalytics: (zoneId, since, until) => api.get(`/zones/${zoneId}/analytics?since=${since || -10080}&until=${until || 0}`),
    purgeCache: (zoneId, options) => api.post(`/zones/${zoneId}/purge-cache`, options),
    getRecordCounts: (zoneId) => api.get(`/zones/${zoneId}/record-counts`),
    getNotes: (zoneId) => api.get(`/zones/${zoneId}/notes`),
    createNote: (zoneId, data) => api.post(`/zones/${zoneId}/notes`, data),
    deleteNote: (zoneId, noteId) => api.delete(`/zones/${zoneId}/notes/${noteId}`),
};

export const validationAPI = {
    checkNameservers: (domain) => api.get(`/validate/nameservers/${domain}`),
    checkDNS: (domain, type = 'A') => api.get(`/validate/dns/${domain}?type=${type}`),
    validateAll: () => api.get('/validate/all'),
};

export const healthAPI = {
    check: () => api.get('/health'),
};

export const chatAPI = {
    sendMessage: (messages) => api.post('/chat', { messages }),
};

export const modelsAPI = {
    getAll: () => api.get('/models'),
    getCurrent: () => api.get('/models/current'),
    setCurrent: (model) => api.put('/models/current', { model }),
};

export const statsAPI = {
    getRecordStats: () => api.get('/stats/records'),
};

export const recordsAPI = {
    checkPropagation: (domain, type) => api.get(`/records/propagation/${domain}?type=${type}`),
    search: (query, type) => api.get(`/records/search?q=${encodeURIComponent(query)}${type ? '&type=' + type : ''}`),
};

export const auditAPI = {
    getLogs: (params = {}) => api.get('/audit', { params }),
};

export const logsAPI = {
    getAll: (params = {}) => api.get('/logs', { params }),
    getStats: () => api.get('/logs/stats'),
};

export const dnssecAPI = {
    getStatus: (zoneId) => api.get(`/dnssec/${zoneId}`),
};

export const sslAPI = {
    getStatus: (zoneId) => api.get(`/ssl/${zoneId}`),
};

export const apiKeysAPI = {
    getAll: () => api.get('/keys'),
    create: (name) => api.post('/keys', { name }),
    toggle: (id, active) => api.patch(`/keys/${id}/toggle`, { active }),
    delete: (id) => api.delete(`/keys/${id}`),
};

export const usersAPI = {
    getAll: () => api.get('/users'),
    approve: (id) => api.patch(`/users/${id}/approve`),
    block: (id) => api.patch(`/users/${id}/block`),
    pending: (id) => api.patch(`/users/${id}/pending`),
    promote: (id) => api.patch(`/users/${id}/promote`),
    demote: (id) => api.patch(`/users/${id}/demote`),
    delete: (id) => api.delete(`/users/${id}`),
};

export default api;
